import { useEffect, useRef, useState, type ComponentType } from "react"
import { NavLink, useLocation } from "react-router"
import {
  Activity,
  BarChart3,
  ClipboardList,
  Compass,
  LayoutDashboard,
  Music2,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  Wrench,
} from "lucide-react"

import {
  ADMIN_REQUESTS_IDLE_POLL_MS,
  useAdminAccountRequests,
  useAdminTopUpRequests,
  useMyAccountRequests,
} from "@/api/hooks/requests"
import { useAuth } from "@/auth/AuthProvider"
import {
  BUYER_ACCOUNT_COMPLETIONS_IDLE_POLL_MS,
  countUnseenCompletedAccountRequests,
  useSeenAccountCompletions,
} from "@/lib/seenAccountCompletions"
import { cn } from "@/lib/utils"

const SIDEBAR_EXPANDED_KEY = "tt.sidebarExpanded.v1"

interface SidebarProps {
  /** Mobile overlay open state. Desktop rail is always visible. */
  mobileOpen: boolean
  onMobileClose: () => void
  /** Expanded (labeled) state for the desktop rail. Mobile drawer is always labeled. */
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  /** Admin panel link is hidden unless the current user is an admin. */
  isAdmin?: boolean
}

interface SidebarEntry {
  to: string
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>
  label: string
  adminOnly?: boolean
  badgeCount?: number
  badgeAriaLabel?: string
}

const FOOTER_ENTRIES: SidebarEntry[] = [
  { to: "/requests", icon: ClipboardList, label: "Requests", adminOnly: true },
  { to: "/activity", icon: Activity, label: "Activity", adminOnly: true },
  { to: "/admin", icon: Settings, label: "Admin Panel", adminOnly: true },
]

function visible(e: SidebarEntry, isAdmin: boolean) {
  if (e.adminOnly) return isAdmin
  return true
}

function SidebarNavRow({
  entry,
  showLabel,
  onNavigate,
}: {
  entry: SidebarEntry
  showLabel: boolean
  onNavigate?: () => void
}) {
  const { pathname } = useLocation()
  const creosActive =
    entry.to.startsWith("/creos") && pathname.startsWith("/creos")
  const badge =
    entry.badgeCount && entry.badgeCount > 0
      ? entry.badgeCount > 99
        ? "99+"
        : String(entry.badgeCount)
      : null
  const Icon = entry.icon

  return (
    <NavLink
      to={entry.to}
      title={showLabel ? undefined : entry.label}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          // Icon sits in a fixed w-14 column so it stays centered in the
          // collapsed rail while labels clip in from the right on expand.
          "relative flex items-center rounded-lg py-2.5 text-sm font-medium transition-colors",
          isActive || creosActive
            ? "bg-[#7488e8]/20 text-[#9eabff]"
            : "text-[#c8c8c8] hover:bg-white/5 hover:text-white",
        )
      }
    >
      <span className="relative flex w-14 shrink-0 items-center justify-center">
        <Icon className="h-5 w-5" aria-hidden />
        {/* Dot must sit on the icon column — a row-right absolute is clipped
            when the collapsed rail overflows at w-14. */}
        {!showLabel && badge ? (
          <span
            className="absolute right-2.5 top-0 h-2 w-2 rounded-full bg-[#dc2626]"
            aria-label={entry.badgeAriaLabel ?? `${badge} new requests`}
          />
        ) : null}
      </span>
      <span
        className={cn(
          "flex min-w-0 flex-1 items-center justify-between gap-2 overflow-hidden whitespace-nowrap pr-3",
          "transition-opacity duration-100 ease-out",
          showLabel ? "opacity-100" : "opacity-0",
        )}
        aria-hidden={!showLabel}
      >
        <span>{entry.label}</span>
        {badge ? (
          <span
            className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[#dc2626] px-1.5 py-0.5 text-[11px] font-bold leading-none text-white"
            aria-label={entry.badgeAriaLabel ?? `${badge} new requests`}
          >
            {badge}
          </span>
        ) : null}
      </span>
    </NavLink>
  )
}

export function readSidebarExpanded(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_EXPANDED_KEY) === "1"
  } catch {
    return false
  }
}

export function writeSidebarExpanded(expanded: boolean) {
  try {
    window.localStorage.setItem(SIDEBAR_EXPANDED_KEY, expanded ? "1" : "0")
  } catch {
    /* ignore */
  }
}

export function Sidebar({
  mobileOpen,
  onMobileClose,
  expanded,
  onExpandedChange,
  isAdmin = false,
}: SidebarProps) {
  const { user } = useAuth()
  // On viewports below md the drawer is always labeled; desktop uses `expanded`.
  // Match CSS: treat unknown/jsdom as labeled when mobileOpen so tests see text.
  const [isDesktop, setIsDesktop] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    const sync = () => setIsDesktop(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  // Collapsed desktop rail: hover peeks labels as an overlay (does not pin).
  const [hoverOpen, setHoverOpen] = useState(false)
  const hoverLeaveTimer = useRef<number | null>(null)

  const clearHoverLeaveTimer = () => {
    if (hoverLeaveTimer.current != null) {
      window.clearTimeout(hoverLeaveTimer.current)
      hoverLeaveTimer.current = null
    }
  }

  useEffect(() => () => clearHoverLeaveTimer(), [])

  const overlayHover = isDesktop && !expanded
  // Pinned expand, or temporary hover peek while collapsed.
  const visuallyExpanded = expanded || (isDesktop && hoverOpen)
  const showLabel = !isDesktop || visuallyExpanded

  const handleRailMouseEnter = () => {
    if (!isDesktop || expanded) return
    clearHoverLeaveTimer()
    setHoverOpen(true)
  }

  const handleRailMouseLeave = () => {
    if (!isDesktop || expanded) return
    clearHoverLeaveTimer()
    // Short delay so leaving via a gap doesn't flicker; keep snappy.
    hoverLeaveTimer.current = window.setTimeout(() => {
      setHoverOpen(false)
      hoverLeaveTimer.current = null
    }, 80)
  }

  const topUpsQuery = useAdminTopUpRequests({
    enabled: isAdmin,
    refetchInterval: ADMIN_REQUESTS_IDLE_POLL_MS,
  })
  const accountsQuery = useAdminAccountRequests({
    enabled: isAdmin,
    refetchInterval: ADMIN_REQUESTS_IDLE_POLL_MS,
  })
  const myAccountRequests = useMyAccountRequests({
    enabled: Boolean(user?.id),
    refetchInterval: BUYER_ACCOUNT_COMPLETIONS_IDLE_POLL_MS,
  })
  const seenCompletions = useSeenAccountCompletions(user?.id)

  const pendingRequestsCount =
    (topUpsQuery.data?.data ?? []).filter((r) => r.status === "pending").length +
    (accountsQuery.data?.data ?? []).filter((r) => r.status === "pending")
      .length

  const unseenCompletedAccountCount = countUnseenCompletedAccountRequests(
    myAccountRequests.data?.data ?? [],
    seenCompletions.data,
  )

  const primary: SidebarEntry[] = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/adsmanager", icon: BarChart3, label: "Ads Manager" },
    { to: "/form", icon: Plus, label: "Create Campaign" },
    { to: "/offers", icon: Compass, label: "Offers" },
    {
      to: "/accounts",
      icon: Music2,
      label: "Accounts",
      badgeCount: unseenCompletedAccountCount,
      badgeAriaLabel: `${unseenCompletedAccountCount} completed account request${
        unseenCompletedAccountCount === 1 ? "" : "s"
      }`,
    },
    { to: "/tools", icon: Wrench, label: "Tools" },
    {
      to: "/creos/uniquify",
      icon: Palette,
      label: "CreOS",
    },
  ].filter((e) => visible(e, isAdmin))

  const footer = FOOTER_ENTRIES.filter((e) => visible(e, isAdmin)).map(
    (entry) =>
      entry.to === "/requests"
        ? { ...entry, badgeCount: pendingRequestsCount }
        : entry,
  )

  return (
    <>
      <button
        type="button"
        aria-label="Close navigation"
        tabIndex={mobileOpen ? 0 : -1}
        onClick={onMobileClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity md:hidden",
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      {/* Collapsed: narrow slot + hover flyout overlay. Pinned: full rail width. */}
      <aside
        className={cn(
          "h-full shrink-0",
          "transition-[width,transform] duration-150 ease-out",
          // Keep positioned so z-index wins over sticky campaign cells (z-10/20).
          "fixed inset-y-0 left-0 z-[60] w-72 md:relative md:z-[60]",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          overlayHover ? "md:w-14" : "md:w-56",
        )}
      >
        <div
          onMouseEnter={handleRailMouseEnter}
          onMouseLeave={handleRailMouseLeave}
          className={cn(
            "flex h-full flex-col overflow-hidden border-r border-[#4a4a4a] bg-[#2a2a2a]",
            "transition-[width,box-shadow] duration-150 ease-out",
            overlayHover
              ? cn(
                  "md:absolute md:inset-y-0 md:left-0 md:z-[60]",
                  visuallyExpanded
                    ? "md:w-56 md:shadow-[8px_0_28px_rgba(0,0,0,0.45)]"
                    : "md:w-14 md:shadow-none",
                )
              : "md:w-full",
          )}
        >
          {/* Fixed expanded width on desktop; parent overflow clips while collapsing. */}
          <div className="flex h-full w-72 flex-col md:w-56">
            <div
              className={cn(
                "flex h-12 shrink-0 items-center border-b border-[#4a4a4a]",
                showLabel ? "justify-between px-3" : "w-14 justify-center",
              )}
            >
              <span
                className={cn(
                  "truncate text-sm font-semibold text-white transition-opacity duration-100 ease-out",
                  showLabel ? "opacity-100" : "pointer-events-none absolute opacity-0",
                )}
              >
                ARCH Hub
              </span>
              <button
                type="button"
                onClick={() => {
                  if (isDesktop) {
                    clearHoverLeaveTimer()
                    setHoverOpen(false)
                    onExpandedChange(!expanded)
                  } else {
                    onMobileClose()
                  }
                }}
                aria-label={
                  isDesktop
                    ? expanded
                      ? "Collapse navigation"
                      : "Expand navigation"
                    : "Close"
                }
                className="shrink-0 rounded-md p-1.5 text-[#b0b0b0] transition-colors hover:bg-white/5 hover:text-white"
              >
                {isDesktop ? (
                  expanded ? (
                    <PanelLeftClose className="h-4 w-4" aria-hidden />
                  ) : (
                    <PanelLeftOpen className="h-4 w-4" aria-hidden />
                  )
                ) : (
                  <span className="px-1 text-lg leading-none" aria-hidden>
                    ×
                  </span>
                )}
              </button>
            </div>

            <nav className="flex min-h-0 flex-1 flex-col gap-1 py-3">
              <div className="flex flex-col gap-0.5 px-0">
                {primary.map((entry) => (
                  <SidebarNavRow
                    key={entry.to}
                    entry={entry}
                    showLabel={showLabel}
                    onNavigate={isDesktop ? undefined : onMobileClose}
                  />
                ))}
              </div>
              {footer.length > 0 && (
                <div className="mt-auto flex flex-col gap-0.5 border-t border-[#4a4a4a] pt-3">
                  {footer.map((entry) => (
                    <SidebarNavRow
                      key={entry.to}
                      entry={entry}
                      showLabel={showLabel}
                      onNavigate={isDesktop ? undefined : onMobileClose}
                    />
                  ))}
                </div>
              )}
            </nav>
          </div>
        </div>
      </aside>
    </>
  )
}

/** Initial expanded state from localStorage (client-only). */
export function useSidebarExpandedState(): [
  boolean,
  (expanded: boolean) => void,
] {
  const [expanded, setExpanded] = useState(false)
  useEffect(() => {
    setExpanded(readSidebarExpanded())
  }, [])
  const update = (next: boolean) => {
    setExpanded(next)
    writeSidebarExpanded(next)
  }
  return [expanded, update]
}
